import { useEffect } from "react";
import "./App.css";
import * as THREE from "three";
import { Reflector } from "three/examples/jsm/Addons.js";
import { Easing, Tween, update as updateTween } from "tween";

const App = () => {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(
    75,
    window.innerWidth / window.innerHeight,
    0.1,
    1000
  );

  // === Optimized WebGLRenderer setup ===
  const renderer = new THREE.WebGLRenderer({
    antialias: false,
    powerPreference: "high-performance",
    stencil: false,
    depth: true,
    alpha: false,
    precision: "highp",
    logarithmicDepthBuffer: true,
  });
  // ensure consistent output color space
  if ("outputColorSpace" in renderer) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  const loadingManager = new THREE.LoadingManager();
  loadingManager.onLoad = () => {
    showLogo(0, true);
  };

  // Optimized texture loader helper
  const textureLoader = new THREE.TextureLoader(loadingManager);
  const loadTexture = (path, noMipmaps = false) => {
    const tex = textureLoader.load(path);
    // set sRGB color space for color-correct display
    if ("colorSpace" in tex) tex.colorSpace = THREE.SRGBColorSpace;

    if (noMipmaps) {
      tex.generateMipmaps = false;
      tex.minFilter = THREE.LinearFilter;
    } else {
      tex.minFilter = THREE.LinearMipmapLinearFilter;
    }
    tex.magFilter = THREE.LinearFilter;
    return tex;
  };

  const parentNode = new THREE.Object3D();
  const allImagesData = [
    {
      title: "Unbound Truth",
      artist: "SpiritualScientist",
      collection: "Stray Thoughts",
      path: "/images/unbound-truth.webp",
      widthRatio: 2,
      heightRatio: 2.75,
      magnify: 1.2,
      zoomMagnify: 1.3,
      collectionInfo: "ST",
      link: "https://foundation.app/mint/eth/0xEab986c3f6FB5B0D1c2833716fDF5F8B8e13361b/1",
      isAnimated: true,
      videoLink: "/textures/UT.mp4",
      CTAText: "Bid (0.25 ETH)",
    },
    {
      title: "Behemoth's Birth",
      artist: "SpiritualScientist",
      collection: "Stray Thoughts",
      path: "/images/behemoth-birth.webp",
      widthRatio: 2,
      heightRatio: 2.75,
      magnify: 1.2,
      zoomMagnify: 1.3,
      collectionInfo: "ST",
      link: "https://foundation.app/mint/eth/0xEab986c3f6FB5B0D1c2833716fDF5F8B8e13361b/4",
      CTAText: "Bid (0.232 ETH)",
    },
    {
      title: "Behemoth's Discipline",
      artist: "SpiritualScientist",
      collection: "Stray Thoughts",
      path: "/images/behemoth-discipline.webp",
      widthRatio: 2,
      heightRatio: 2.75,
      magnify: 1.2,
      zoomMagnify: 1.3,
      collectionInfo: "ST",
      link: "https://foundation.app/mint/eth/0xEab986c3f6FB5B0D1c2833716fDF5F8B8e13361b/5",
      CTAText: "Bid (0.3 ETH)",
    },
    {
      title: "Behemoth's Palace",
      artist: "SpiritualScientist",
      collection: "Stray Thoughts",
      path: "/images/behemoth-palace.webp",
      widthRatio: 2,
      heightRatio: 2.75,
      magnify: 1.2,
      zoomMagnify: 1.3,
      collectionInfo: "ST",
      link: "https://foundation.app/mint/eth/0xEab986c3f6FB5B0D1c2833716fDF5F8B8e13361b/3",
      CTAText: "Bid (0.215 ETH)",
    },
    {
      title: "Everything/Nothing",
      artist: "SpiritualScientist",
      collection: "Reality Check",
      path: "/images/everything-nothing.webp",
      widthRatio: 2.25,
      heightRatio: 4,
      magnify: 0.8,
      zoomMagnify: 1.3,
      collectionInfo: "RC",
      link: "https://foundation.app/mint/base/0xC9E04F75e766C85F73f6e3EDC445E4f0755dc3Ad/2",
      CTAText: "Bid (0.1111 ETH)",
    },
    {
      title: "Birth of an Ophanim",
      artist: "SpiritualScientist",
      collection: "Blueprints from God",
      path: "/images/birth-of-ophanim.webp",
      widthRatio: 2.25,
      heightRatio: 4,
      magnify: 0.8,
      zoomMagnify: 1.3,
      collectionInfo: "BfG",
      link: "https://opensea.io/assets/ethereum/0x495f947276749ce646f68ac8c248420045cb7b5e/86736703122780243083394649627257269305560688445110559906526774306843186429972",
      isAnimated: true,
      videoLink: "/textures/BoO.mp4",
      CTAText: "View (Sold Out)",
    },
    {
      title: "Window to the Soul",
      artist: "SpiritualScientist",
      collection: "Reality Check",
      path: "/images/window-to-the-soul.webp",
      widthRatio: 2.25,
      heightRatio: 4,
      magnify: 0.8,
      zoomMagnify: 1.3,
      collectionInfo: "RC",
      link: "https://foundation.app/mint/base/0xC9E04F75e766C85F73f6e3EDC445E4f0755dc3Ad/1",
      CTAText: "Bid (0.1111 ETH)",
    },
    {
      title: "Unrestricted Culture",
      artist: "SpiritualScientist",
      collection: "Interactive Thoughts",
      path: "/images/unrestricted-culture.webp",
      widthRatio: 3,
      heightRatio: 3,
      magnify: 0.8,
      zoomMagnify: 1.3,
      collectionInfo: "IT",
      link: "https://objkt.com/tokens/KT1Sw9GRw5kPstFbrZBHhkEcxYduYLHUNYih/0",
      CTAText: "Buy Edition (10 Tez)",
      hasWIP: true,
      videoLink: "/textures/UCultureWIP.mp4",
    },
    {
      title: "Unstable Faith",
      artist: "SpiritualScientist",
      collection: "Reality Check",
      path: "/images/unstable-faith.webp",
      widthRatio: 3,
      heightRatio: 3,
      magnify: 0.8,
      zoomMagnify: 1.3,
      link: "https://opensea.io/assets/ethereum/0x7725638c8336692ee71ce0e638e18e10215e3edb/2",
      CTAText: "View (Sold Out)",
      isAnimated: true,
      videoLink: "/textures/UF.mp4",
    },
    {
      title: "Embrace of the Eternal Will",
      artist: "SpiritualScientist",
      collection: "Crypts of a Wandering Artist",
      path: "/images/embrace-of-eternal-will.webp",
      widthRatio: 3,
      heightRatio: 3,
      magnify: 0.8,
      zoomMagnify: 1.3,
      collectionInfo: "CWA",
      link: "https://foundation.app/mint/eth/0x82DC2Cdac86418B1067f0c464A82b47ee712B139/3",
      CTAText: "View (Sold Out)",
    },
    {
      title: "Significance of Bonds",
      artist: "SpiritualScientist",
      collection: "Deeper Crypts of a Wandering Artist",
      path: "/images/s-of-bonds.webp",
      widthRatio: 3,
      heightRatio: 3,
      magnify: 0.8,
      zoomMagnify: 1.3,
      collectionInfo: "DCWA",
      link: "https://opensea.io/assets/ethereum/0x1d5cc759112c809697eee0e28f7534b39226628c/1",
      CTAText: "View (Sold Out)",
    },
    {
      title: "Spatial Nexus",
      artist: "SpiritualScientist",
      collection: "Spatial Nexus",
      path: "/images/guardian-of-light.webp",
      widthRatio: 3,
      heightRatio: 3,
      magnify: 0.8,
      zoomMagnify: 1.3,
      link: "https://foundation.app/mint/eth/0x95E13BC8C906eB09Ff1c66Cb444afFc611F8a4f3",
      CTAText: "Buy Edition (0.01 ETH)",
    },
  ];

  const links = {
    I: "http://instagram.com/_u/spiritual_scientist_art/",
    X: "https://x.com/spiritualS_art",
    F: "https://foundation.app/@SpiritualScientist",
  };

  let currentArtwork = allImagesData[0];
  let storedZoomObjectArray = [];
  let actionObjects = {
    sideDrawerOpen: false,
    artInfoOpen: false,
    fullWidthArtwork: false,
    artistInfoOpen: false,
    selectedImageData: allImagesData[0],
    isVideoPlaying: false,
  };

  let miniBooleans = {
    isRotating: false,
    isZooming: false,
  };

  const artInfoSectionsWIP = [
    "artworkImageDiv",
    "artworkDesc",
    "collectionDesc",
  ];
  const artInfoSections = ["artworkImageDiv"];

  var xDown = null;
  var yDown = null;

  let artworkNames =
    "Unbound Truth · Behemoth's Birth · Behemoth's Discipline · Behemoth's Palace · Everything/Nothing · Birth of an Ophanim · Window to the Soul · Unrestricted Culture · Unstable Faith · Embrace of the Eternal Will · Significance of Bonds · Spatial Nexus · ";

  const getTouches = (evt) => {
    return evt.touches || evt.originalEvent.touches;
  };

  const handleTouchStart = (evt) => {
    const firstTouch = getTouches(evt)[0];
    xDown = firstTouch.clientX;
    yDown = firstTouch.clientY;
  };

  const handleTouchMove = (evt) => {
    let actionsToChecks = JSON.parse(JSON.stringify(actionObjects));
    if (!xDown || !yDown) {
      return;
    }
    var xUp = evt.touches[0].clientX;
    var yUp = evt.touches[0].clientY;

    var xDiff = xDown - xUp;
    var yDiff = yDown - yUp;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (!miniBooleans.isRotating && !somethingIsHappening()) {
        rotateGallery(xDiff > 0 ? -1 : 1);
      }
      if (actionsToChecks.artInfoOpen) {
        //do something
      }
    } else {
      if (yDiff > 0) {
        if (actionsToChecks.artistInfoOpen && !actionsToChecks.artInfoOpen) {
          toggleDrawers("artistInfoOpen");
        }
      } else {
        if (!actionsToChecks.artistInfoOpen && !actionsToChecks.artInfoOpen) {
          toggleDrawers("artistInfoOpen");
        }
      }
    }
    xDown = null;
    yDown = null;
  };

  const raycaster = new THREE.Raycaster();
  const mouseNDC = new THREE.Vector2();

  useEffect(() => {
    initGalleryScene();
    setImageViewData(allImagesData[0]);
  }, []);

  const initGalleryScene = () => {
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    let documentToRender = document.getElementById("mainContainer");
    documentToRender.appendChild(renderer.domElement);
    if (window.innerWidth >= 570) {
      let leftArrow = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 0.3, 1),
        new THREE.MeshStandardMaterial({
          map: loadTexture("/images/left.png", true),
          transparent: true,
        })
      );
      leftArrow.position.set(-1.3, 0, -3);
      leftArrow.rotation.y = 0.4;
      leftArrow.name = "leftArrow";
      let rightArrow = new THREE.Mesh(
        new THREE.BoxGeometry(0.3, 0.3, 1),
        new THREE.MeshStandardMaterial({
          map: loadTexture("/images/right.png", true),
          transparent: true,
        })
      );
      rightArrow.position.set(1.3, 0, -3);
      rightArrow.rotation.y = -0.4;
      rightArrow.name = "rightArrow";
      scene.add(leftArrow);
      scene.add(rightArrow);
    }
    allImagesData.forEach((imageItem, index) => {
      let texturePath = getTexturePath(imageItem.path);
      let texture = loadTexture(texturePath);
      let baseNode = new THREE.Object3D();
      let artwork = new THREE.Mesh(
        new THREE.BoxGeometry(
          imageItem.widthRatio * (imageItem.magnify || 1),
          imageItem.heightRatio * (imageItem.magnify || 1),
          0.1
        ),
        new THREE.MeshStandardMaterial({ map: texture })
      );

      artwork.name = "artwork";
      artwork.data = imageItem;
      artwork.leftArrow =
        allImagesData[
          index - 1 > allImagesData.length - 1
            ? 0
            : index - 1 < 0
            ? allImagesData.length - 1
            : index - 1
        ];
      artwork.rightArrow =
        allImagesData[
          index + 1 > allImagesData.length + 1
            ? 0
            : index + 1 < 0
            ? allImagesData.length + 1
            : index + 1
        ];
      let border = new THREE.Mesh(
        new THREE.BoxGeometry(
          imageItem.widthRatio * (imageItem.magnify || 1) + 0.2,
          imageItem.heightRatio * (imageItem.magnify || 1) + 0.2,
          0.09
        ),
        new THREE.MeshStandardMaterial({ color: 0x303030 })
      );
      border.name = "border";
      border.position.z = -5.5;
      artwork.position.z = -5.5;
      baseNode.add(border);
      baseNode.add(artwork);
      baseNode.rotation.y = index * ((2 * Math.PI) / allImagesData.length);
      parentNode.add(baseNode);
    });

    scene.add(parentNode);

    let spotlight = new THREE.SpotLight(0xffffff, 100.0, 10.0, 0.65, 0.5);
    spotlight.position.set(0, 5, 0);
    spotlight.target.position.set(0, 0.5, -5);
    scene.add(spotlight);
    scene.add(spotlight.target);

    const mirror = new Reflector(new THREE.CircleGeometry(10), {
      color: 0x404040,
      textureHeight: window.innerHeight,
      textureWidth: window.innerWidth,
    });
    mirror.position.y = -1.75;
    mirror.rotateX(-Math.PI / 2);
    scene.add(mirror);

    renderer.setAnimationLoop(animate);

    document.getElementById("title").innerHTML = currentArtwork.title;
    document.getElementById("artist").innerHTML = currentArtwork.collection;

    // Debounced resize
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      }, 150);
    });

    // Unified click + hover raycasting
    const handleRaycast = (clientX, clientY, isClick = false) => {
      mouseNDC.x = (clientX / window.innerWidth) * 2 - 1;
      mouseNDC.y = -(clientY / window.innerHeight) * 2 + 1;
      raycaster.setFromCamera(mouseNDC, camera);

      let interactions = raycaster.intersectObject(scene, true);
      if (isClick) {
        if (interactions?.length > 0) {
          if (
            interactions?.[0]?.object?.name.toLowerCase().includes("arrow") &&
            !miniBooleans.isRotating
          ) {
            rotateGallery(interactions[0].object.name == "rightArrow" ? -1 : 1);
          }
          if (checkInteractionPair(interactions)) {
            document.getElementById("artworkImage").src = getTexturePath(
              interactions[
                interactions.findIndex((item) => item.object?.data?.path)
              ].object.data.path
            );
            if (!somethingIsHappening()) {
              toggleDrawers("artInfoOpen");
            }
          }
        }
      } else {
        if (interactions?.length == 2) {
          if (checkInteractionPair(interactions)) {
            if (!miniBooleans.isZooming) {
              storedZoomObjectArray = interactions;
              zoomInArtwork();
            }
          }
        } else {
          zoomInArtwork(true);
        }
      }
    };

    window.addEventListener("click", (e) => {
      if (!somethingIsHappening()) {
        handleRaycast(e.clientX, e.clientY, true);
      }
    });

    window.addEventListener("mousemove", (e) => {
      if (!isMobile()) {
        if (!somethingIsHappening()) {
          handleRaycast(e.clientX, e.clientY, false);
        }
      } else {
        // mobile move still triggers zoom behavior if needed (kept same behavior)
        handleRaycast(e.clientX, e.clientY, false);
      }
    });

    document.addEventListener("touchstart", handleTouchStart, false);
    document.addEventListener("touchmove", handleTouchMove, false);
  };

  const rotateGallery = (direction) => {
    if (!somethingIsHappening()) {
      let index = allImagesData.findIndex(
        (imageItem) => imageItem.title == currentArtwork.title
      );
      let nextIndex =
        index + direction > allImagesData.length - 1
          ? 0
          : index + direction < 0
          ? allImagesData.length - 1
          : index + direction;
      currentArtwork = allImagesData[nextIndex];
      const finalPosition = -(
        direction *
        ((2 * Math.PI) / allImagesData.length)
      );
      new Tween(parentNode.rotation)
        .to({ y: parentNode.rotation.y + finalPosition })
        .easing(Easing.Quadratic.InOut)
        .start()
        .onStart(() => {
          document.getElementById("title").style.opacity = 0;
          document.getElementById("artist").style.opacity = 0;
          changeActionObjects("isRotating", true);
        })
        .onComplete(() => {
          document.getElementById("title").innerHTML =
            allImagesData[nextIndex].title;
          document.getElementById("artist").innerHTML =
            allImagesData[nextIndex].collection;
          document.getElementById("title").style.opacity = 1;
          document.getElementById("artist").style.opacity = 1;
          changeActionObjects("isRotating", false);
          setImageViewData(allImagesData[nextIndex]);
          changeActionObjects("selectedImageData", allImagesData[nextIndex]);
        });
    }
  };

  const zoomInArtwork = (isReset = false) => {
    if (storedZoomObjectArray.length) {
      if (!isReset) {
        storedZoomObjectArray.forEach((objectItem) => {
          new Tween(objectItem.object.position)
            .to({ z: -4.5 })
            .easing(Easing.Quadratic.InOut)
            .start()
            .onStart(() => {
              changeActionObjects("isZooming", true);
            })
            .onComplete(() => {
              changeActionObjects("isZooming", false);
            });
          if (objectItem.object.name == "border") {
            var col = new THREE.Color(0xffffff);
            new Tween(objectItem.object.material.color)
              .to({ r: col.r, g: col.g, b: col.b })
              .easing(Easing.Quadratic.InOut)
              .start()
              .onStart(() => {
                changeActionObjects("isZooming", false);
              });
          }
        });
      } else {
        storedZoomObjectArray.forEach((objectItem) => {
          new Tween(objectItem.object.position)
            .to({ z: -5.5 })
            .easing(Easing.Quadratic.InOut)
            .start();
          if (objectItem.object.name == "border") {
            var col = new THREE.Color(0x303030);
            new Tween(objectItem.object.material.color)
              .to({ r: col.r, g: col.g, b: col.b })
              .easing(Easing.Quadratic.InOut)
              .start()
              .onStart(() => {
                changeActionObjects("isZooming", true);
              })
              .onComplete(() => {
                changeActionObjects("isZooming", false);
              });
          }
        });
        storedZoomObjectArray = [];
      }
    }
  };

  const checkInteractionPair = (interactions) => {
    let interactionsMap = interactions
      .map((item) => item.object.name)
      .toString();
    let isCurrentArtwork =
      interactions.findIndex(
        (item) => item.object?.data?.title == currentArtwork.title
      ) != -1;
    return (
      (interactionsMap == "artwork,border" ||
        interactionsMap == "border,artwork") &&
      isCurrentArtwork
    );
  };

  const animate = () => {
    updateTween();
    renderer.render(scene, camera);
  };

  const openDrawer = () => {
    zoomInArtwork(true);
    toggleDrawers(actionObjects.artInfoOpen ? "artInfoOpen" : "artistInfoOpen");
  };

  const toggleDrawers = (objectName) => {
    let transformType =
      objectName == "artistInfoOpen" ? "translateY(" : "translateX(";
    let transformDirection = objectName == "artistInfoOpen" ? "vh" : "vw";
    document.getElementById(objectName).style.transform = actionObjects[
      objectName
    ]
      ? `${transformType}-100${transformDirection})`
      : `${transformType}0${transformDirection})`;
    document.getElementById("closeButton").style.transform = !actionObjects[
      objectName
    ]
      ? "scale(1)"
      : "scale(0)";
    document.getElementById("closeButton").style.opacity = !actionObjects[
      objectName
    ]
      ? 1
      : 0;
    if (objectName == "artInfoOpen") {
      magnify("artworkImage", actionObjects?.selectedImageData?.zoomMagnify || 1.5);
      document.getElementById('playIcon').src = 'images/play.png';
      document.getElementById('playText').innerHTML = 'Play ' + (actionObjects?.selectedImageData?.hasWIP ? 'WIP' : '');
      toggleVideo();
      if (actionObjects[objectName]) {
        toggleViewButtons(0);

        artInfoSections.forEach((item, index) => {
          document.getElementById(item).style.transform = "scale(1)";
          document.getElementById(item).style.opacity = 1;
          if (index != 0) {
            document.getElementById(item).style.transform = "translateX(-100%)";
          }
        });
      } else {
        toggleViewButtons(1);
      }
      changeActionObjects("isVideoPlaying", false);
    }
    changeActionObjects(objectName, !actionObjects[objectName]);
  };

  const getTexturePath = (path) => {
    let pathSplit = path.split("/");
    let endSplit = pathSplit[pathSplit.length - 1].split(".");
    pathSplit[1] = "textures";
    pathSplit[pathSplit.length - 1] =
      endSplit[0] + "-texture." + endSplit[endSplit.length - 1];
    return pathSplit.toString().replaceAll(",", "/");
  };

  const changeActionObjects = async (objectName, value) => {
    if (objectName == "isRotating" || objectName == "isZooming") {
      miniBooleans[objectName] = value;
    } else {
      actionObjects[objectName] = value;
    }
  };

  const setImageViewData = (data) => {
    let videoButtonElement = document.getElementById("videoButton");
    let buyButton = document.getElementById("buyButton");

    document.getElementById("buyText").innerHTML = data.CTAText;
    document.getElementById("playText").innerHTML =
      "Play " + (data?.hasWIP ? " WIP" : "");

    videoButtonElement.style.display =
      data.isAnimated || data.hasWIP ? "flex" : "none";
    videoButtonElement.style.width =
      window.innerWidth >= 700 ? "auto" : "calc(50%)";
    buyButton.style.width =
      window.innerWidth >= 700
        ? "auto"
        : data.isAnimated || data.hasWIP
        ? "calc(50%)"
        : "calc(100%)";
  };

  const somethingIsHappening = () => {
    return (
      Object.keys(actionObjects).filter(
        (item) =>
          actionObjects[item] &&
          item != "fullWidthArtwork" &&
          item != "selectedImageData"
      ).length > 0
    );
  };

  const toggleArtworkSize = () => {
    document.getElementById("artworkImage").style.objectFit =
      !actionObjects.fullWidthArtwork ? "cover" : "contain";
    document.getElementById("artworkVideo").style.objectFit =
      !actionObjects.fullWidthArtwork ? "cover" : "contain";

    document.getElementById("zoomIcon").src = !actionObjects.fullWidthArtwork
      ? "images/minimize.png"
      : "images/maximize.png";
    changeActionObjects("fullWidthArtwork", !actionObjects.fullWidthArtwork);
  };

  const redirectToLink = (path) => {
    window.open(
      path != "art" ? links[path] : actionObjects.selectedImageData.link,
      "_blank"
    );
  };

  const toggleViewButtons = (range, actOnClose = false) => {
    document.getElementById("zoomButton").style.opacity = range;
    document.getElementById("zoomButton").style.transform = `scale(${range})`;

    if(actOnClose) {
      document.getElementById("closeButton").style.opacity = range;
      document.getElementById("closeButton").style.transform = `scale(${range})`;
    }

    let viewButtons = document.getElementsByClassName("viewButton");
    if (viewButtons.length > 0) {
      for (let i = 0; i < viewButtons.length; i++) {
        viewButtons[i].style.opacity = range;
        viewButtons[i].style.transform = `scale(${range})`;
      }
    }
  };

  const playVideo = () => {
    let videoElement = document.getElementById("artworkVideo");
    let videoText = document.getElementById('playText');

    let playIcon = document.getElementById('playIcon');

    if (videoElement) {
      if (videoElement.src.split('/')[videoElement.src.split('/').length - 1] == actionObjects?.selectedImageData?.videoLink?.split('/')[actionObjects?.selectedImageData?.videoLink?.split('/').length - 1]) {
        if (actionObjects.isVideoPlaying) {
          videoElement.pause();
          videoText.innerHTML = 'Play ' + (actionObjects?.selectedImageData?.hasWIP ? 'WIP' : '');
          playIcon.src = 'images/play.png';
        }
        else {
          videoElement.play();
          videoText.innerHTML = 'Pause ' + (actionObjects?.selectedImageData?.hasWIP ? 'WIP' : '');
          playIcon.src = 'images/pause.png';
        }
      }
      else {
        videoText.innerHTML = 'Pause ' + (actionObjects?.selectedImageData?.hasWIP ? 'WIP' : '');
        playIcon.src = 'images/pause.png';
        toggleVideo(actionObjects?.selectedImageData || null);
      }
      changeActionObjects("isVideoPlaying", !actionObjects.isVideoPlaying);
      
    }
  };

  const showLogo = (range, isShowLoader = false) => {
    let logoElement = document.getElementById("logo");
    let loaderElement = document.getElementById("loader");

    if (logoElement) {
      logoElement.style.transform = `scale(${range > 0 ? 1 : 0.1})`;
      logoElement.style.opacity = range;
      logoElement.style.pointerEvents = range > 0 ? "auto" : "none";
      logoElement.style.animationPlayState = range > 0 ? "running" : "paused";
    }

    if (isShowLoader && loaderElement) {
      document.getElementById("loader").style.opacity = range;
      toggleArtworkNamesAnimation(range > 0 ? "running" : "paused");
    }
  };

  const toggleArtworkNamesAnimation = (state) => {
    let toToggle = [
      "artworkNames",
      "artworkNamesReverse",
      "artworkNamesLower",
      "artworkNamesReverseLower",
    ];
    toToggle.forEach((item) => {
      document.getElementById(item).style.animationPlayState = state;
    });
  };

  const isMobile = () => {
    var check = false;
    (function (a, b) {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  };

  const toggleVideo = (data = null) => {
    let videoElement = document.getElementById('artworkVideo');
    let imageElement = document.getElementById('artworkImage');

    videoElement.src = data ? data?.videoLink || null : null;
    videoElement.style.display = data?.videoLink ? 'flex' : 'none';
    imageElement.style.display = !data?.videoLink ? 'flex' : 'none';

    videoElement.onloadstart = () => {
      if (data) {
        showLogo(1);
      }
    };
    videoElement.onloadeddata = () => {
      videoElement.style.opacity = 1;
      showLogo(0);
    };

  }

  const magnify = (imgID, zoom = 2) => {
    const img = document.getElementById(imgID);
    const container = img.parentElement;
    const glassSize = 220;
    let glass = container.querySelector(".img-magnifier-glass");
    if (!glass) {
      glass = document.createElement("DIV");
      glass.setAttribute("class", "img-magnifier-glass");
      container.appendChild(glass);
    }
  
    // Ensure container positioned relatively
    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative";
    }
  
    const w = glassSize / 2;
    const h = glassSize / 2;
    const bw = 3;
  
    function getVisibleImageRect() {
      const containerRect = container.getBoundingClientRect();
      const naturalRatio = img.naturalWidth / img.naturalHeight;
      const containerRatio = containerRect.width / containerRect.height;
  
      let width, height, top, left;
  
      if (getComputedStyle(img).objectFit === "contain") {
        if (naturalRatio > containerRatio) {
          width = containerRect.width;
          height = width / naturalRatio;
          top = (containerRect.height - height) / 2;
          left = 0;
        } else {
          height = containerRect.height;
          width = height * naturalRatio;
          top = 0;
          left = (containerRect.width - width) / 2;
        }
      } else if (getComputedStyle(img).objectFit === "cover") {
        if (naturalRatio > containerRatio) {
          height = containerRect.height;
          width = height * naturalRatio;
          top = 0;
          left = (containerRect.width - width) / 2;
        } else {
          width = containerRect.width;
          height = width / naturalRatio;
          top = (containerRect.height - height) / 2;
          left = 0;
        }
      } else {
        width = containerRect.width;
        height = containerRect.height;
        top = 0;
        left = 0;
      }
  
      return { width, height, top, left };
    }
  
    const getCursorPos = (e) => {
      const containerRect = container.getBoundingClientRect();
      const visibleRect = getVisibleImageRect();
  
      let x, y;
      if (e.touches) {
        x = e.touches[0].clientX - containerRect.left - visibleRect.left;
        y = e.touches[0].clientY - containerRect.top - visibleRect.top;
      } else {
        x = e.clientX - containerRect.left - visibleRect.left;
        y = e.clientY - containerRect.top - visibleRect.top;
      }
  
      x = Math.max(0, Math.min(x, visibleRect.width));
      y = Math.max(0, Math.min(y, visibleRect.height));
  
      return { x, y, visibleRect, containerRect };
    };
  
    const moveMagnifier = (e) => {
      e.preventDefault();
      hideForMagnifier(0);
      const { x: rawX, y: rawY, visibleRect, containerRect } = getCursorPos(e);
  
      const x = Math.min(Math.max(rawX, w), visibleRect.width - w);
      const y = Math.min(Math.max(rawY, h), visibleRect.height - h);
  
      const leftPos = Math.min(
        Math.max(visibleRect.left + x - w, 0),
        containerRect.width - glassSize
      );
      const topPos = Math.min(
        Math.max(visibleRect.top + y - h, 0),
        containerRect.height - glassSize
      );
  
      glass.style.display = "block";
      img.classList.add("dimmed");
  
      glass.style.backgroundImage = `url('${img.src}')`;
      glass.style.backgroundSize = `${visibleRect.width * zoom}px ${
        visibleRect.height * zoom
      }px`;
  
      glass.style.left = leftPos + "px";
      glass.style.top = topPos + "px";
  
      glass.style.backgroundPosition = `-${x * zoom - w + bw}px -${y * zoom - h + bw}px`;
  
      const touchingLeft = leftPos <= 1;
      const touchingRight = leftPos >= containerRect.width - glassSize - 1;
      const touchingTop = topPos <= 1;
      const touchingBottom = topPos >= containerRect.height - glassSize - 1;
  
      const topLeft = touchingTop || touchingLeft;
      const topRight = touchingTop || touchingRight;
      const bottomRight = touchingBottom || touchingRight;
      const bottomLeft = touchingBottom || touchingLeft;
  
      const borderRadius = `
        ${topLeft ? 0 : "50%"} 
        ${topRight ? 0 : "50%"} 
        ${bottomRight ? 0 : "50%"} 
        ${bottomLeft ? 0 : "50%"}
      `.trim();
  
      glass.style.borderRadius = borderRadius;
    };
  
    const showMagnifier = (e) => {
      moveMagnifier(e);
    };
  
    const hideMagnifier = () => {
      glass.style.display = "none";
      img.classList.remove("dimmed");
      glass.style.borderRadius = "50%";
      hideForMagnifier(1);
    };
  
    img.addEventListener("mousemove", moveMagnifier);
    container.addEventListener("mousemove", moveMagnifier);
    img.addEventListener("mouseleave", hideMagnifier);
    container.addEventListener("mouseleave", hideMagnifier);
  
    img.addEventListener("touchstart", showMagnifier);
    img.addEventListener("touchmove", moveMagnifier);
    img.addEventListener("touchend", hideMagnifier);
    img.addEventListener("touchcancel", hideMagnifier);
  };

  const hideForMagnifier = (range) => {
    if(isMobile()) {
      const artworkElement = document.getElementById("artworkImage");
      if(artworkElement?.style?.objectFit == "cover") {
        toggleViewButtons(range, true);
      }
    }

  }
  
  
  return (
    <div id="rootContainer">
      <div id="logo">
        <img
          className="loader"
          width="100"
          height="100"
          src="images/logo.jpg"
        ></img>
      </div>
      <div id="artInfoOpen">
        <div id="artworkImageDiv">
          <img
            style={{
              objectFit: actionObjects.fullWidthArtwork ? "cover" : "contain",
            }}
            id="artworkImage"
          ></img>
          <video
            style={{
              objectFit: actionObjects.fullWidthArtwork ? "cover" : "contain",
            }}
            id="artworkVideo"
            autoPlay
            loop
            muted
            playsInline
          ></video>
        </div>
      </div>
      <div id="sideDrawerOpen"></div>
      <div id="closeButton">
        <img
          id="closeIcon"
          src="images/close.png"
          onClick={() => openDrawer()}
        ></img>
      </div>
      <div className="action-button-div">
        <div
          className="action-button-bg viewButton"
          onClick={() => playVideo()}
          id="videoButton"
        >
          <img id="playIcon" src="images/play.png"></img>
          <span id="playText"></span>
        </div>
        <div
          className="action-button-bg viewButton"
          onClick={() => redirectToLink("art")}
          id="buyButton"
          style={{
            width:
              window.innerWidth >= 700
                ? "auto"
                : actionObjects?.selectedImageData?.isAnimated
                ? "calc(50%)"
                : "calc(100%)",
          }}
        >
          <img id="viewIcon" src="images/share.png"></img>
          <span id="buyText"></span>
        </div>
      </div>

      <div id="zoomButton" onClick={() => toggleArtworkSize()}>
        <img id="zoomIcon" src="images/maximize.png"></img>
      </div>
      <div id="loader">
        <div id="artworkNamesContainer">
          <div id="artworkNames">
            <span>{artworkNames}</span>
          </div>
          <div id="artworkNamesReverse">
            <span>{artworkNames}</span>
          </div>
        </div>
        <div id="artworkNamesContainerReverse">
          <div id="artworkNamesLower">
            <span>{artworkNames}</span>
          </div>
          <div id="artworkNamesReverseLower">
            <span>{artworkNames}</span>
          </div>
        </div>
      </div>
      <div id="footerNav">
        <img
          className="menu"
          src="images/header-logo.png"
          onClick={() => toggleDrawers("artistInfoOpen")}
        ></img>
      </div>
      <div id="mainContainer"></div>
      <div id="artworkTextHeader">
        <h1 id="title"></h1>
      </div>
      <div id="artworkTextFooter">
        <h2 id="artist"></h2>
      </div>
      <div id="artistInfoOpen">
        <div className="artistImage">
          <div className="neo-box-bright d-flex v-center h-center">
            <img src="images/logo.jpg" className="artistImageLogo"></img>
          </div>
        </div>
        <div className="artistInfo">
          <div className="neo-box d-flex v-center h-center flex-column">
            <div className="d-flex w-100" style={{ color: "white" }}>
              <span id="artistText">
                Hello, Visitor! 🤍
                <br /> <br /> Allow me to introduce myself. I'm an artist who
                specializes in creating intricate, monochrome artworks that
                visually represent a deeper spiritual narrative. <br />
                <br /> You might be wondering - why monochrome? Well, my
                creations are an extension of my beliefs and my very existence.
                I'm a firm believer of duality and of the fact that the
                'reality' we experience is subjected to perspective - what might
                be dark to one, could be light to others - therefore, grey.{" "}
                <br />
                <br />
                I'd love for you to join me in my journey to achieve all that
                (and more)!
              </span>
            </div>
            <div className="d-flex w-100">
              <div
                style={{
                  width: "33%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  className="media-buttons"
                  onClick={() => redirectToLink("I")}
                >
                  <img src="images/instagram.png" width={30}></img>
                </div>
              </div>
              <div
                style={{
                  width: "33%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  className="media-buttons"
                  onClick={() => redirectToLink("X")}
                >
                  <img src="images/x.png" width={30}></img>
                </div>
              </div>
              <div
                style={{
                  width: "33%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <div
                  className="media-buttons"
                  onClick={() => redirectToLink("F")}
                >
                  <img src="images/foundation.png" width={40}></img>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div id="footerDisclaimer">
        <span className="padding-10">
          Artworks and Gallery by SpiritualScientist with 🤍
        </span>
      </div>

    </div>

  );
};

export default App;
